import React, { useState, useEffect } from 'react';

import AddIcon from '@mui/icons-material/Add';
import { types } from '@sunwisesoftware/sunwise-template-core';
import {
    Box,
    Button,
    Container as MaterialContainer,
    Grid,
    Tab,
    Tabs,
    UiDialog as Dialog,
} from '@sunwisesoftware/sunwise-ui';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { HeaderContainer, TitleIcon } from 'common/components';

import * as actions from './actions';
import ListItems from './components/ListItems';
import TemplateForm from './components/TemplateForm';
import * as selectors from './selectors';

const Container = ({
    fetchTemplates,
    handleClickBulkItems,
    handleClickDuplicate,
    handleClickRename,
    handleCloseModal,
    handleSaveRow,
    isFetching,
    isFetchingBulk,
    isOpenModal,
    paginationData,
    prepareCreate,
    prepareUpdate,
    templates,
}) => {
    const { t } = useTranslation();
    const [filterData, setFilterData] = useState({
        orderBy: 'created_at',
        page: 0,
        pageSize: 25,
        sortBy: 'desc',
        status: 'active',
        type: 2,
    });
    const [currentFilterData, setCurrentFilterData] = useState({});

    useEffect(() => {
        if (!isEqual(currentFilterData, filterData)) {
            setCurrentFilterData(filterData);
            fetchTemplates(filterData);
        }
    }, [filterData]);

    const tabsContent = [
        {
            label: t('Document', { count: 2 }),
            value: types.SMART_DOCUMENTS_TYPE,
        },
        { label: t('Report', { count: 2 }), value: types.REPORT_TYPE },
    ];

    return (
        <MaterialContainer maxWidth={false}>
            <HeaderContainer sxDivider={{ mb: 0 }}>
                <Grid container alignItems="center" spacing={2}>
                    <Grid size="grow">
                        <TitleIcon title={t('Template', { count: 2 })} />
                    </Grid>

                    <Button
                        endIcon={<AddIcon />}
                        onClick={() => prepareCreate(filterData.type)}
                        sx={{ width: { md: 'auto', xs: '100%' } }}
                        variant="outlined"
                    >
                        {t('Add Template')}
                    </Button>
                </Grid>
            </HeaderContainer>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs
                    onChange={(_, value) =>
                        setFilterData({ ...filterData, type: value })
                    }
                    value={filterData.type}
                    variant="scrollable"
                >
                    {tabsContent.map(({ label, value }) => (
                        <Tab
                            disabled={isFetching || isFetchingBulk}
                            key={value}
                            label={label}
                            value={value}
                        />
                    ))}
                </Tabs>
            </Box>

            <ListItems
                canDelete={true}
                canUpdate={true}
                filterData={filterData}
                filterItems={fetchTemplates}
                handleClickBulkItems={handleClickBulkItems}
                handleClickDuplicate={handleClickDuplicate}
                handleClickRename={handleClickRename}
                handleClickUpdate={prepareUpdate}
                handleSaveRow={handleSaveRow}
                isFetching={isFetching || isFetchingBulk}
                items={templates}
                paginationData={paginationData}
                setFilterData={setFilterData}
            />

            <Dialog
                onClose={handleCloseModal}
                open={isOpenModal}
                size="sm"
                title={t('Create template')}
            >
                <TemplateForm filterData={filterData} />
            </Dialog>
        </MaterialContainer>
    );
};

Container.propTypes = {
    fetchTemplates: PropTypes.func,
    handleClickBulkItems: PropTypes.func,
    handleClickDuplicate: PropTypes.func,
    handleClickRename: PropTypes.func,
    handleCloseModal: PropTypes.func,
    handleSaveRow: PropTypes.func,
    isFetching: PropTypes.bool,
    isFetchingBulk: PropTypes.bool,
    isOpenModal: PropTypes.bool,
    paginationData: PropTypes.object,
    prepareCreate: PropTypes.func,
    prepareUpdate: PropTypes.func,
    templates: PropTypes.array,
};

const mapStateToProps = createStructuredSelector({
    isFetching: selectors.getIsFetchingTemplates,
    isFetchingBulk: selectors.getIsFetchingBulk,
    isOpenModal: selectors.getIsOpenModal,
    paginationData: selectors.getTemplatesPagination,
    templates: selectors.getTemplatesData,
});

const mapDispatchToProps = (dispatch) => ({
    fetchTemplates: (filterData) =>
        dispatch(actions.fetchTemplates(filterData)),
    handleClickBulkItems: (ids, value, filterData, callback) =>
        dispatch(actions.prepareBulkItems(ids, value, filterData, callback)),
    handleClickDuplicate: (product) =>
        dispatch(actions.prepareDuplicate(product)),
    handleClickRename: (filterData, product) =>
        dispatch(actions.prepareRename(filterData, product)),
    handleCloseModal: () => dispatch(actions.closeModal()),
    handleSaveRow: (filterData, item) =>
        dispatch(actions.updateProduct(filterData, item)),
    prepareCreate: (type) => dispatch(actions.prepareCreate(type)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Container);
